/**
 * CallDispositionForm — lichte bevestiging na ophangen.
 *
 * De AI bepaalt categorie + samenvatting async (minuten ná ophangen), dus dit
 * scherm dwingt niets af. De enige tijdgevoelige beslissing is "direct
 * terugbellen?"; notitie en categorie zijn optioneel bijsturen. Laat je de
 * categorie op "(automatisch bepalen)" staan, dan blijft de AI leidend.
 */

import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { CallRecord } from '../../lib/telephony';
import { BELREDENEN } from '../../lib/telephony/belredenen';

interface Props {
  call: CallRecord;
  onDone: () => void;
}

export function CallDispositionForm({ call, onDone }: Props) {
  // Leeg = "(automatisch bepalen)" → geen note_category sturen, AI wint.
  const [category, setCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [makeCallback, setMakeCallback] = useState(false);
  const [callbackAt, setCallbackAt] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function submit() {
    if (saving) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const auth: Record<string, string> = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {};

      if (call.callId) {
        // Bouw een PATCH die alleen bevat wat de mens bewust koos. Zonder
        // keuzes laten we alles aan de AI-pijplijn.
        const patch: Record<string, unknown> = { callId: call.callId };
        let hasPatch = false;
        if (notes.trim()) { patch.notes = notes.trim(); hasPatch = true; }
        if (category) { patch.note_category = category; hasPatch = true; }
        if (makeCallback) { patch.callback_status = 'pending'; hasPatch = true; }

        if (hasPatch) {
          const r = await fetch('/api/telephony/callback', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...auth },
            body: JSON.stringify(patch),
          });
          if (!r.ok) {
            const err = await r.json().catch(() => ({ error: 'Opslaan mislukt' }));
            throw new Error(err.error || 'Opslaan mislukt');
          }
        }
      }

      if (makeCallback && call.callId) {
        const r = await fetch('/api/inbox/create-callback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth },
          body: JSON.stringify({
            callId: call.callId,
            callbackDueAt: callbackAt || null,
            customerName: call.customer?.name ?? null,
            phone: call.remoteNumber,
            brand: call.brand,
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: 'Terugbel-thread aanmaken mislukt' }));
          throw new Error(err.error || 'Terugbel-thread aanmaken mislukt');
        }
      }

      onDone();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const headingId = `disposition-heading-${call.clientCallId}`;
  const categoryId = `disposition-category-${call.clientCallId}`;
  const notesId = `disposition-notes-${call.clientCallId}`;
  const callbackAtId = `disposition-callback-at-${call.clientCallId}`;

  return (
    <form
      role="form"
      aria-labelledby={headingId}
      className="p-3 space-y-3 border-t border-gray-200 bg-white"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div id={headingId} className="text-xs font-medium text-gray-500">
        Gesprek met {call.customer?.name ?? call.remoteNumber}
      </div>

      {/* Primair: de enige tijdgevoelige beslissing. */}
      <label className="flex items-center gap-2 text-sm font-medium text-gray-800 bg-gray-50 border border-gray-200 rounded px-3 py-2">
        <input
          type="checkbox"
          checked={makeCallback}
          onChange={(e) => setMakeCallback(e.target.checked)}
        />
        Direct terugbellen? Zet in de terugbel-werkvoorraad
      </label>

      {makeCallback && (
        <div>
          <label htmlFor={callbackAtId} className="block text-xs font-medium text-gray-600 mb-1">
            Terugbelmoment (optioneel)
          </label>
          <input
            id={callbackAtId}
            type="datetime-local"
            value={callbackAt}
            onChange={(e) => setCallbackAt(e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </div>
      )}

      <p className="text-xs text-gray-500">
        Categorie en samenvatting worden automatisch bepaald zodra het gesprek verwerkt is;
        hier alleen bijsturen als het nodig is.
      </p>

      {/* Optioneel bijsturen. */}
      <div>
        <label htmlFor={notesId} className="block text-xs font-medium text-gray-600 mb-1">
          Notitie (optioneel)
        </label>
        <textarea
          id={notesId}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          placeholder="Alleen als er iets belangrijks besproken is dat de AI niet oppikt"
        />
      </div>

      <div>
        <label htmlFor={categoryId} className="block text-xs font-medium text-gray-600 mb-1">
          Categorie (optioneel)
        </label>
        <select
          id={categoryId}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="">(automatisch bepalen)</option>
          {BELREDENEN.map((b) => (
            <option key={b.code} value={b.code}>{b.label}</option>
          ))}
        </select>
      </div>

      {errorMsg && (
        <div role="alert" className="text-xs text-red-600">
          {errorMsg}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-60"
          disabled={saving}
        >
          Overslaan
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-60"
        >
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>
    </form>
  );
}
